import { useCallback } from "react";
import { useAtomValue } from "jotai";
import { trackEvent } from "common/analytics";
import { mapViewportAtom } from "@/state/atoms";
import { useSearchPageParams } from "common/hooks/business/home/useSearchPageParams";

// "sociability" = places with data (clustered). "declutter" = same data venues
// but rendered as individual pins that thin out by zoom via Google's collision
// engine (no clustering). "all" = every venue + native Google POIs.
export type MapMode = "sociability" | "declutter" | "all";

/**
 * Shared map-mode state: derives the current mode from search params and exposes
 * a setter that also pins the search to the live map viewport. Used by both the
 * list header toggle (desktop) and the on-map toggle (mobile).
 */
export function useMapMode() {
  const { searchPageParams, setSearchPageParams } = useSearchPageParams();
  const mapViewport = useAtomValue(mapViewportAtom);
  const mapMode: MapMode =
    searchPageParams.map_mode === "all"
      ? "all"
      : searchPageParams.map_mode === "declutter"
        ? "declutter"
        : "sociability";

  const changeMapMode = useCallback(
    (mode: MapMode) => {
      const vp = mapViewport;
      const viewportParams = vp
        ? {
            latitude: vp.lat.toString(),
            longitude: vp.lng.toString(),
            zoom: vp.zoom.toString(),
            searchThisAreaClicked: "no",
            autoZoom: "no",
            map_ne_lat: vp.ne.lat.toString(),
            map_ne_lng: vp.ne.lng.toString(),
            map_sw_lat: vp.sw.lat.toString(),
            map_sw_lng: vp.sw.lng.toString(),
          }
        : {};
      setSearchPageParams((prev) => ({
        ...prev,
        ...viewportParams,
        map_mode: mode,
        // Both "with data" views (sociability + declutter) query mapped venues;
        // only "all" drops the filter to include everything.
        mapping_status: mode === "all" ? undefined : "mapped",
        section_filter: undefined,
        tagging_mode: "off",
        unixTimestamp: Math.floor(Date.now() / 1000),
      }));

      // search_results_pin_view_changed — the "All places" <-> "Places with data" map
      // pin toggle. Scoped to the search results page (the event's page) and only when
      // the value actually changes. A results-wide toggle has no single place, so the
      // place dimensions are "" (mapper/user is the sibling toggle in VenueList).
      if (
        mode !== mapMode &&
        typeof window !== "undefined" &&
        window.location.pathname.startsWith("/search")
      ) {
        trackEvent("search_results_pin_view_changed", {
          brand_id: "",
          brand_name: "",
          business_id: "",
          place_name: "",
          pin_data_type: mode === "all" ? "all" : "with_data",
          previous_pin_data_type: mapMode === "all" ? "all" : "with_data",
        });
      }
    },
    [setSearchPageParams, mapViewport, mapMode],
  );

  return { mapMode, changeMapMode };
}
